import type { InventoryListing } from "@workspace/api-client-react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  buildInventoryExportRows,
  exportToCsv,
  inventoryExportFilename,
} from "@/lib/csv-export";

interface ExportCsvButtonProps {
  // The currently-visible (filtered) listings to export.
  listings: InventoryListing[];
  // Context label for the file name (event name, account context, or search
  // term); slugified and date-stamped into "<base>-<date>.csv".
  filenameBase: string;
}

// Exports the rows the workspace is currently showing to a CSV download.
// Disabled (with an explanatory tooltip) when there is nothing to export.
export function ExportCsvButton({ listings, filenameBase }: ExportCsvButtonProps) {
  const count = listings.length;
  const disabled = count === 0;

  const handleExport = () => {
    if (disabled) return;
    const rows = buildInventoryExportRows(listings);
    exportToCsv(rows, inventoryExportFilename(filenameBase));
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 text-xs px-2.5 bg-card rounded-full"
      disabled={disabled}
      title={
        disabled
          ? "No rows to export"
          : `Export ${count} row${count === 1 ? "" : "s"} to CSV`
      }
      onClick={handleExport}
    >
      <Download className="h-3 w-3 mr-1" />
      Export CSV
    </Button>
  );
}
